/**
 * AI Insights Panel Component
 * Displays AI-generated performance insights and recommendations
 * Phase 6.3 - AI Insights Section
 */

import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  Divider,
  Avatar,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  Psychology as AIIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Warning as WarningIcon,
  CheckCircle as SuccessIcon,
  Lightbulb as IdeaIcon,
  Speed as SpeedIcon,
  Group as GroupIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';

export interface AIInsight {
  id: string;
  category: 'performance' | 'opportunity' | 'risk' | 'recommendation';
  title: string;
  description: string;
  impact: 'high' | 'medium' | 'low';
  metric?: string;
  metricValue?: string;
  trend?: 'up' | 'down' | 'stable';
  actionable?: boolean;
  suggestedAction?: string;
}

export interface AIInsightsPanelProps {
  insights: AIInsight[];
  loading?: boolean;
  title?: string;
}

const AIInsightsPanel: React.FC<AIInsightsPanelProps> = ({
  insights,
  loading = false,
  title = 'AI Performance Insights'
}) => {
  // Get category icon
  const getCategoryIcon = (category: AIInsight['category']) => {
    switch (category) {
      case 'performance':
        return <SpeedIcon />;
      case 'opportunity':
        return <IdeaIcon />;
      case 'risk':
        return <WarningIcon />;
      case 'recommendation':
        return <AIIcon />;
      default:
        return <AIIcon />;
    }
  };

  // Get category color
  const getCategoryColor = (category: AIInsight['category']) => {
    switch (category) {
      case 'performance':
        return 'primary';
      case 'opportunity':
        return 'success';
      case 'risk':
        return 'error';
      case 'recommendation':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get impact color
  const getImpactColor = (impact: AIInsight['impact']) => {
    switch (impact) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  // Get trend icon
  const getTrendIcon = (trend?: 'up' | 'down' | 'stable') => {
    switch (trend) {
      case 'up':
        return <TrendingUpIcon sx={{ color: 'success.main', fontSize: 18 }} />;
      case 'down':
        return <TrendingDownIcon sx={{ color: 'error.main', fontSize: 18 }} />;
      default:
        return null;
    }
  };

  // Group insights by category
  const groupedInsights = insights.reduce((acc, insight) => {
    if (!acc[insight.category]) {
      acc[insight.category] = [];
    }
    acc[insight.category].push(insight);
    return acc;
  }, {} as Record<string, AIInsight[]>);

  const categoryLabels: Record<string, string> = {
    performance: 'Performance Insights',
    opportunity: 'Opportunities',
    risk: 'Risk Alerts',
    recommendation: 'AI Recommendations'
  };

  if (loading) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <AIIcon color="primary" />
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
        </Box>
        {[1, 2, 3].map((i) => (
          <Skeleton
            key={i}
            variant="rectangular"
            height={100}
            sx={{ mb: 2, borderRadius: 1 }}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
        <Avatar sx={{ bgcolor: 'primary.main', width: 36, height: 36 }}>
          <AIIcon sx={{ fontSize: 20 }} />
        </Avatar>
        <Box>
          <Typography variant="h6" fontWeight="600">
            {title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {insights.length} insights generated
          </Typography>
        </Box>
      </Box>

      {/* Insights by Category */}
      {Object.entries(groupedInsights).map(([category, categoryInsights]) => (
        <Box key={category} sx={{ mb: 3 }}>
          {/* Category Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
            {getCategoryIcon(category as AIInsight['category'])}
            <Typography variant="subtitle1" fontWeight="600">
              {categoryLabels[category] || category}
            </Typography>
            <Chip
              label={categoryInsights.length}
              size="small"
              color={getCategoryColor(category as AIInsight['category'])}
              sx={{ height: 20, fontSize: '0.7rem' }}
            />
          </Box>

          {/* Insights List */}
          <List disablePadding>
            {categoryInsights.map((insight, index) => (
              <Paper
                key={insight.id}
                sx={{
                  mb: 1.5,
                  p: 2,
                  border: 1,
                  borderColor: 'divider',
                  '&:hover': {
                    boxShadow: 2,
                    borderColor: `${getCategoryColor(insight.category)}.main`
                  },
                  transition: 'all 0.2s'
                }}
              >
                <ListItem disablePadding sx={{ alignItems: 'flex-start' }}>
                  <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                    <Avatar
                      sx={{
                        width: 32,
                        height: 32,
                        bgcolor: `${getCategoryColor(insight.category)}.light`,
                        color: `${getCategoryColor(insight.category)}.main`
                      }}
                    >
                      {getCategoryIcon(insight.category)}
                    </Avatar>
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography variant="subtitle2" fontWeight="600">
                          {insight.title}
                        </Typography>
                        <Chip
                          label={insight.impact.toUpperCase()}
                          size="small"
                          color={getImpactColor(insight.impact)}
                          variant="outlined"
                          sx={{ height: 18, fontSize: '0.65rem' }}
                        />
                        {insight.trend && getTrendIcon(insight.trend)}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 1, lineHeight: 1.5 }}
                        >
                          {insight.description}
                        </Typography>

                        {/* Metric Display */}
                        {insight.metric && insight.metricValue && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              bgcolor: 'action.hover',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              mr: 1,
                              mb: 1
                            }}
                          >
                            <Typography variant="caption" color="text.secondary">
                              {insight.metric}:
                            </Typography>
                            <Typography variant="caption" fontWeight="600">
                              {insight.metricValue}
                            </Typography>
                          </Box>
                        )}

                        {/* Suggested Action */}
                        {insight.actionable && insight.suggestedAction && (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 0.5,
                              mt: 1,
                              p: 1,
                              bgcolor: 'primary.50',
                              borderRadius: 1,
                              border: 1,
                              borderColor: 'primary.200'
                            }}
                          >
                            <SuccessIcon
                              sx={{ fontSize: 16, color: 'primary.main', mt: 0.25 }}
                            />
                            <Typography variant="caption" color="primary.main">
                              <strong>Suggested Action:</strong> {insight.suggestedAction}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </Paper>
            ))}
          </List>
        </Box>
      ))}

      {/* Empty State */}
      {insights.length === 0 && (
        <Box
          sx={{
            textAlign: 'center',
            py: 4,
            color: 'text.secondary'
          }}
        >
          <AIIcon sx={{ fontSize: 48, mb: 1, opacity: 0.5 }} />
          <Typography variant="body2">
            No insights available yet. Check back later as AI analyzes more data.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AIInsightsPanel;
